import { reconcileWidgetOrder, type SummaryWidgetStackItem } from "#app/widgets/summary/summary-widget-stack";

/**
 * The project summary's section roster — ONE list the detail view, the create
 * form and the project-type settings page all read, so the sections an admin
 * orders on the type are exactly the sections a member sees.
 *
 * Instances are hardcoded (never persisted); what the server holds is the
 * TYPE's layout over them (`IssueType.sectionOrder` / `hiddenSections`) and
 * the viewer's folds (`myPreferences.collapsedSummarySections`).
 */
export const ISSUE_WIDGET_STACK: SummaryWidgetStackItem[] = [
  {
    id: "issue-properties",
    type: "issue-properties",
    x: 0,
    y: 0,
    w: 12,
    h: 2,
    config: {},
    schemaVersion: 1,
    hideHeader: true,
  },
  // Leads the sections by default — a description, when a type wants one,
  // is the first thing to read — but is OFF until a type turns it on: it
  // snapshots a surface that already owns the full experience, so leaving
  // it off costs no reach.
  {
    id: "issue-description",
    type: "issue-description",
    x: 0,
    y: 2,
    w: 12,
    h: 2,
    config: {},
    schemaVersion: 1,
    collapsible: true,
    hideable: true,
  },
  {
    id: "issue-updates",
    type: "issue-updates",
    x: 0,
    y: 4,
    w: 12,
    h: 3,
    config: {},
    schemaVersion: 1,
    collapsible: true,
  },
  {
    id: "issue-custom-fields",
    type: "issue-custom-fields",
    x: 0,
    y: 7,
    w: 12,
    h: 4,
    config: {},
    schemaVersion: 1,
    collapsible: true,
  },
  // Comments is not in the stack: the Activity tab owns comments on a
  // Project, and a summary copy of the thread said the same thing twice.
  // Attachments is listed but LOCKED — never rendered, never offered on the
  // type — until the summary has a settled attachments story; the Media tab
  // owns it today.
  {
    id: "issue-attachments",
    type: "issue-attachments",
    x: 0,
    y: 14,
    w: 12,
    h: 3,
    config: {},
    schemaVersion: 1,
    collapsible: true,
    locked: "Coming soon",
  },
];

export const ISSUE_PROPERTIES_WIDGET = ISSUE_WIDGET_STACK[0]!;
export const ISSUE_SORTABLE_WIDGETS = ISSUE_WIDGET_STACK.slice(1);

/** Hidden until a type turns them on. */
export const ISSUE_DEFAULT_HIDDEN_WIDGETS = ["issue-description", "issue-attachments"];

/** The sections the type settings page lists: everything but the locked. */
export const ISSUE_TYPE_LAYOUT_SECTIONS = ISSUE_SORTABLE_WIDGETS.filter(
  (widget) => !widget.locked,
);

/** Create mode renders these when the type shows them; comments and
 *  attachments need a saved issue. */
export const CREATE_MODE_WIDGET_TYPES = ["issue-description", "issue-custom-fields"];

export type SummaryTypeLayout = {
  sectionOrder: readonly string[];
  hiddenSections: readonly string[];
};

/**
 * A type's layout, resolved against the roster that exists today: the type's
 * order for the sections it knows (unknown ids drop out, new sections splice
 * in at their default neighborhood), and which sections it hides.
 *
 * A type that never saved a layout (`sectionOrder` empty) shows the default
 * roster with the default-hidden sections off. Once a type has an order it is
 * the type's full statement: `hiddenSections` alone decides what is off, so
 * an admin can turn a default-hidden section on. Locked sections stay off
 * either way, and a section the type cannot hide (`hideable` unset) stays on
 * whatever an older row says.
 */
export function resolveTypeSectionLayout(
  layout: SummaryTypeLayout | null | undefined,
  sortableWidgets: readonly SummaryWidgetStackItem[] = ISSUE_SORTABLE_WIDGETS,
  defaultHiddenTypes: readonly string[] = ISSUE_DEFAULT_HIDDEN_WIDGETS,
): { orderedWidgets: SummaryWidgetStackItem[]; hidden: Record<string, boolean> } {
  const hasLayout = Boolean(layout && layout.sectionOrder.length > 0);
  const hiddenIds = new Set(hasLayout ? layout!.hiddenSections : defaultHiddenTypes);
  const hidden: Record<string, boolean> = {};
  for (const widget of sortableWidgets) {
    hidden[widget.type] = Boolean(
      widget.locked || (widget.hideable && hiddenIds.has(widget.type)),
    );
  }
  const orderedWidgets = reconcileWidgetOrder(layout?.sectionOrder ?? [], sortableWidgets)
    .map((type) => sortableWidgets.find((widget) => widget.type === type))
    .filter((widget): widget is SummaryWidgetStackItem => Boolean(widget));
  return { orderedWidgets, hidden };
}

/**
 * The property band's movable rows, in their default order: who owns it,
 * where to reach it, then provenance last. Type, Workflow and Stage always
 * lead the band and are not listed. A type may reorder these
 * (`IssueType.propertyOrder`); the create form and the summary render the
 * same order.
 */
export const ORDERABLE_PROPERTY_KEYS = ["assignees", "teams", "address", "source"] as const;
export type OrderablePropertyKey = (typeof ORDERABLE_PROPERTY_KEYS)[number];

/**
 * A type's property order replayed against the keys that exist today: unknown
 * keys drop out, keys the order never saw splice in at their default place.
 * Empty (a type that never reordered) = the default order.
 */
export function resolvePropertyOrder(
  order: readonly string[] | null | undefined,
): OrderablePropertyKey[] {
  const known = new Set<string>(ORDERABLE_PROPERTY_KEYS);
  const saved: OrderablePropertyKey[] = [];
  for (const key of order ?? []) {
    if (known.has(key) && !saved.includes(key as OrderablePropertyKey)) {
      saved.push(key as OrderablePropertyKey);
    }
  }
  const resolved = [...saved];
  ORDERABLE_PROPERTY_KEYS.forEach((key, index) => {
    if (resolved.includes(key)) return;
    let anchor = -1;
    for (let before = index - 1; before >= 0; before -= 1) {
      const found = resolved.indexOf(ORDERABLE_PROPERTY_KEYS[before]!);
      if (found >= 0) {
        anchor = found;
        break;
      }
    }
    resolved.splice(anchor + 1, 0, key);
  });
  return resolved;
}
